/**
 * Patient Detail Page
 *
 * View patient information and studies
 */

import { useState } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  Edit,
  Trash2,
  User,
  Phone,
  Mail,
  MapPin,
  Calendar,
  FileImage,
  Loader2,
  AlertCircle,
} from 'lucide-react'

import { usePatient, usePatientStudies, useDeletePatient } from '@/application/patients'
import { toast } from '@/lib/toast'
import { getErrorMessage } from '@/lib/api'
import { ConfirmDialog } from '@/presentation/components/ui/ConfirmDialog'
import { Button } from '@/presentation/components/ui/Button'
import { Badge } from '@/presentation/components/ui/Badge'
import { Card, CardHeader, CardTitle, CardContent } from '@/presentation/components/ui/Card'
import {
  DOCUMENT_TYPE_LABELS,
  type DocumentType,
} from '@/config/constants'
import { formatFechaLarga, calcularEdad } from '@/lib/utils'

// =============================================================================
// Helper Functions
// =============================================================================

const SEX_LABELS: Record<string, string> = {
  M: 'Masculino',
  F: 'Femenino',
  O: 'Otro',
}

// =============================================================================
// Components
// =============================================================================

interface InfoItemProps {
  icon: React.ReactNode
  label: string
  value: string | null | undefined
}

function InfoItem({ icon, label, value }: InfoItemProps) {
  return (
    <div className="flex items-start gap-3">
      <div className="p-2 rounded-lg bg-[var(--secondary)] text-[var(--foreground-muted)]">
        {icon}
      </div>
      <div>
        <p className="text-sm text-[var(--foreground-muted)]">{label}</p>
        <p className="font-medium text-[var(--foreground)]">
          {value || <span className="text-[var(--foreground-muted)]">No registrado</span>}
        </p>
      </div>
    </div>
  )
}

function LoadingState() {
  return (
    <div className="flex flex-col items-center justify-center h-[60vh]">
      <Loader2 className="h-8 w-8 animate-spin text-[var(--primary)]" />
      <p className="text-[var(--foreground-muted)] mt-4">Cargando paciente...</p>
    </div>
  )
}

function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center h-[60vh] text-center">
      <AlertCircle className="h-12 w-12 text-[var(--destructive)] mb-4" />
      <h3 className="text-lg font-semibold text-[var(--foreground)]">Error al cargar</h3>
      <p className="text-[var(--foreground-muted)] mt-1">{message}</p>
      <Button onClick={onRetry} className="mt-4">
        Reintentar
      </Button>
    </div>
  )
}

// =============================================================================
// Main Component
// =============================================================================

export function PatientDetailPage() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  const { data: patient, isLoading, isError, error, refetch } = usePatient(id)
  const { data: studies } = usePatientStudies(id)
  const deleteMutation = useDeletePatient()

  const handleDelete = () => {
    setDeleteDialogOpen(true)
  }

  const confirmDelete = () => {
    deleteMutation.mutate(id!, {
      onSuccess: () => {
        toast.success('Paciente eliminado')
        setDeleteDialogOpen(false)
        navigate('/patients')
      },
      onError: (error) => {
        toast.error(getErrorMessage(error, 'Error al eliminar paciente'))
        setDeleteDialogOpen(false)
      },
    })
  }

  if (isLoading) return <LoadingState />
  if (isError) return <ErrorState message={error?.message || 'Error desconocido'} onRetry={refetch} />
  if (!patient) return <ErrorState message="Paciente no encontrado" onRetry={refetch} />

  const edad = calcularEdad(patient.birthDate)

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-[var(--foreground)]">
              {patient.firstName} {patient.lastName}
            </h1>
            <p className="text-[var(--foreground-muted)]">
              {DOCUMENT_TYPE_LABELS[patient.document.type as DocumentType]} {patient.document.number}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Link to={`/patients/${id}/edit`}>
            <Button variant="outline">
              <Edit className="h-4 w-4 mr-2" />
              Editar
            </Button>
          </Link>
          <Button
            variant="outline"
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
            className="text-[var(--destructive)] border-[var(--destructive)]/30 hover:bg-[var(--destructive)]/10"
          >
            {deleteMutation.isPending ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Trash2 className="h-4 w-4 mr-2" />
            )}
            Eliminar
          </Button>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Patient Info */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Información Personal
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-6 sm:grid-cols-2">
                <InfoItem
                  icon={<User className="h-4 w-4" />}
                  label="Sexo"
                  value={SEX_LABELS[patient.sex]}
                />
                <InfoItem
                  icon={<Calendar className="h-4 w-4" />}
                  label="Fecha de Nacimiento"
                  value={`${formatFechaLarga(patient.birthDate)}${edad === null ? "" : ` (${edad} años)`}`}
                />
                <InfoItem
                  icon={<Phone className="h-4 w-4" />}
                  label="Teléfono"
                  value={patient.contact?.phone}
                />
                <InfoItem
                  icon={<Mail className="h-4 w-4" />}
                  label="Email"
                  value={patient.contact?.email}
                />
                <InfoItem
                  icon={<MapPin className="h-4 w-4" />}
                  label="Dirección"
                  value={patient.contact?.address}
                />
                <InfoItem
                  icon={<MapPin className="h-4 w-4" />}
                  label="Ciudad"
                  value={patient.contact?.city}
                />
              </div>
            </CardContent>
          </Card>

          {/* Studies Section */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <FileImage className="h-5 w-5" />
                Estudios
              </CardTitle>
              <Link to={`/studies?patientId=${id}`}>
                <Button variant="outline" size="sm">
                  Ver todos
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {!studies?.length ? (
                <div className="text-center py-8">
                  <FileImage className="h-12 w-12 text-[var(--foreground-muted)] mx-auto mb-4" />
                  <p className="text-[var(--foreground-muted)]">
                    No hay estudios registrados
                  </p>
                </div>
              ) : (
                <p className="text-[var(--foreground-muted)]">
                  {studies.length} estudio(s) registrado(s)
                </p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick Stats */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Resumen</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-[var(--foreground-muted)]">Estudios</span>
                <Badge variant="secondary">{studies?.length || 0}</Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-[var(--foreground-muted)]">Registrado</span>
                <span className="text-sm">{formatFechaLarga(patient.createdAt)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-[var(--foreground-muted)]">Actualizado</span>
                <span className="text-sm">{formatFechaLarga(patient.updatedAt)}</span>
              </div>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Acciones Rápidas</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Link to={`/studies?patientId=${id}`} className="block">
                <Button variant="outline" className="w-full justify-start">
                  <FileImage className="h-4 w-4 mr-2" />
                  Ver Estudios
                </Button>
              </Link>
              <Link to={`/patients/${id}/edit`} className="block">
                <Button variant="outline" className="w-full justify-start">
                  <Edit className="h-4 w-4 mr-2" />
                  Editar Información
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Eliminar paciente"
        description="¿Estás seguro de que deseas eliminar este paciente? Esta acción no se puede deshacer."
        confirmLabel="Eliminar"
        cancelLabel="Cancelar"
        variant="danger"
        onConfirm={confirmDelete}
        isLoading={deleteMutation.isPending}
      />
    </div>
  )
}
